import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  Stethoscope,
  Save,
  Edit,
  X,
  FormInput,
} from 'lucide-react';
import { getVisit, getPatient, updateVisitData, client } from '../services/api';
import type { VisitResponse, PatientResponse } from '../services/api/types.gen';
import {
  Badge,
  Button,
  Textarea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { OASISForm, OASISFormData } from '../components/oasis';

const VisitDetail: React.FC = () => {
  const { patientId, visitId } = useParams<{
    patientId: string;
    visitId: string;
  }>();
  const navigate = useNavigate();
  const [visit, setVisit] = useState<VisitResponse | null>(null);
  const [patient, setPatient] = useState<PatientResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('form');

  useEffect(() => {
    const fetchData = async () => {
      if (!visitId || !patientId) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch both visit and patient data in parallel
        const [visitData, patientData] = await Promise.all([
          getVisit({
            path: { visit_id: visitId },
            client,
          }),
          getPatient({
            path: { patient_id: patientId },
            client,
          }),
        ]);

        if (visitData && visitData.data) {
          setVisit(visitData.data);
        }
        if (patientData && patientData.data) {
          setPatient(patientData.data);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load visit details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [visitId, patientId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'default';
      case 'scheduled':
        return 'secondary';
      case 'in_progress':
        return 'outline';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const handleEdit = () => {
    if (!visit) return;

    const jsonString = visit.visit_data
      ? JSON.stringify(visit.visit_data, null, 2)
      : '{}';

    setEditValue(jsonString);
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!visit || !visitId) return;

    try {
      setSaveLoading(true);
      setSaveError(null);

      // Parse the JSON to validate it
      let parsedData: Record<string, unknown>;
      try {
        parsedData = JSON.parse(editValue);
      } catch {
        setSaveError('Invalid JSON format. Please check your syntax.');
        return;
      }

      // Update the visit data with optimistic concurrency control
      const updatedVisit = await updateVisitData({
        path: { visit_id: visitId },
        body: {
          visit_data: parsedData,
        },
        client,
      });

      // Update the local state
      if (updatedVisit && updatedVisit.data) {
        setVisit(updatedVisit.data);
      }
      setIsEditing(false);
      setEditValue('');
    } catch (err) {
      console.error('Error saving visit data:', err);
      setSaveError('Failed to save visit data. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleOASISFormSave = async (formData: OASISFormData) => {
    if (!visit || !visitId) return;

    try {
      setSaveError(null);

      // Update the visit data with optimistic concurrency control
      await updateVisitData({
        path: { visit_id: visitId },
        body: {
          visit_data: formData,
        },
        client,
      });

      // Don't update local visit state after save - the form already has the correct data
      // and updating would overwrite pseudo question selections that aren't persisted
    } catch (err) {
      console.error('Error saving OASIS form data:', err);
      setSaveError('Failed to save form data. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4'></div>
          <p className='text-muted-foreground'>Loading visit details...</p>
        </div>
      </div>
    );
  }

  if (error || !visit || !patient) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Visit</h3>
          <p className='text-muted-foreground mb-4'>
            {error || 'Visit not found'}
          </p>
          <Button
            onClick={() => navigate(`/patients/${patientId}`)}
            variant='outline'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Patient
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => navigate(`/patients/${patientId}`)}
            className='flex items-center'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Patient
          </Button>
          <div>
            <h1 className='text-2xl font-bold'>
              {patient.first_name} {patient.last_name}
            </h1>
            <p className='text-muted-foreground'>
              {visit.visit_type} - {formatDate(visit.visit_date)}
            </p>
          </div>
        </div>
        <Badge
          variant={getStatusBadgeVariant(visit.status)}
          className='text-sm'
        >
          {visit.status}
        </Badge>
      </div>

      {/* Visit Data Section */}
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <div>
            <h2 className='text-lg font-semibold flex items-center gap-2'>
              <Stethoscope className='h-5 w-5' />
              Visit Data
            </h2>
            <p className='text-sm text-muted-foreground'>
              OASIS form and structured visit documentation
            </p>
          </div>
          <div className='flex items-center gap-2'>
            {activeTab === 'json' && !isEditing && (
              <Button
                onClick={handleEdit}
                variant='outline'
                size='sm'
                className='flex items-center gap-2'
              >
                <Edit className='h-4 w-4' />
                Edit JSON
              </Button>
            )}
          </div>
        </div>

        <div className='flex justify-between items-center text-sm text-muted-foreground'>
          <span>Last Updated</span>
          <span className='font-medium'>
            {visit.visit_data_last_updated
              ? formatDateTime(visit.visit_data_last_updated)
              : 'Never'}
          </span>
        </div>

        {saveError && (
          <div className='text-sm text-destructive bg-destructive/10 p-3 rounded-md'>
            {saveError}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className='grid w-full grid-cols-2'>
            <TabsTrigger value='form' className='flex items-center gap-2'>
              <FormInput className='h-4 w-4' />
              OASIS Form
            </TabsTrigger>
            <TabsTrigger value='json' className='flex items-center gap-2'>
              <Edit className='h-4 w-4' />
              JSON Editor
            </TabsTrigger>
          </TabsList>

          <TabsContent value='form' className='mt-6'>
            <OASISForm
              visitId={visitId!}
              visitType={visit.visit_type || 'SOC'}
              initialData={visit.visit_data as OASISFormData}
              onSave={handleOASISFormSave}
            />
          </TabsContent>

          <TabsContent value='json' className='mt-6'>
            <div className='space-y-4'>
              {isEditing ? (
                <div className='space-y-4'>
                  <Textarea
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    placeholder='Enter JSON data...'
                    className='font-mono text-sm min-h-[500px]'
                  />
                  <div className='flex gap-2'>
                    <Button
                      onClick={handleSave}
                      disabled={saveLoading}
                      className='flex items-center gap-2'
                    >
                      {saveLoading ? (
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                      ) : (
                        <Save className='h-4 w-4' />
                      )}
                      {saveLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant='outline'
                      disabled={saveLoading}
                      className='flex items-center gap-2'
                    >
                      <X className='h-4 w-4' />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <pre className='bg-muted p-4 rounded-md text-sm overflow-auto font-mono min-h-[500px]'>
                  {JSON.stringify(visit.visit_data, null, 2)}
                </pre>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default VisitDetail;
